import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import Popup from "./BasePopup";

// Utility functions
function generateNumberArray(begin, month, year) {
  let array = [];
  let end = 30;
  if (month === 2) {
    if ((year % 4 === 0 && year % 100 !== 0) || year % 400 === 0) {
      end = 29;
    } else {
      end = 28;
    }
  } else {
    if ([1, 3, 5, 7, 8, 10, 12].includes(month)) {
      end = 31;
    } else {
      end = 30;
    }
  }
  for (let i = begin; i <= end; i++) {
    array.push((i < 10 ? "0" : "") + i);
  }
  return array;
}

function generateArrayOfYears() {
  const currentYear = new Date().getFullYear();
  const startYear = 1970; // Start from 1970
  let years = [];

  for (let i = startYear; i <= currentYear; i++) {
    years.push(i.toString());
  }
  return years;
}

function generateMonths() {
  return [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
}

const DateTimePopup = ({
  isOpen,
  title,
  onClose,
  onSave,
  dayOptions,
  monthOptions,
  yearOptions,
}) => {
  const currentYear = new Date().getFullYear();
  const defaultYear = currentYear - 18; // Default year set to current year minus 18
  const defaultMonth = monthOptions[0]; // Set default month to January
  const defaultDay = "01"; // First day of the month

  const [selectedDay, setSelectedDay] = useState(defaultDay);
  const [selectedMonth, setSelectedMonth] = useState(defaultMonth);
  const [selectedYear, setSelectedYear] = useState(defaultYear.toString());

  const [days, setDays] = useState(
    generateNumberArray(
      1,
      monthOptions.indexOf(selectedMonth) + 1,
      parseInt(selectedYear)
    )
  );
  const [extendedDays, setExtendedDays] = useState([
    ...generateNumberArray(1, 1, parseInt(selectedYear)),
  ]);

  const dayRef = useRef(null);
  const monthRef = useRef(null);
  const yearRef = useRef(null);

  useEffect(() => {
    const newDays = generateNumberArray(
      1,
      monthOptions.indexOf(selectedMonth) + 1,
      parseInt(selectedYear)
    );
    setDays(newDays);
    setExtendedDays([...newDays, ...newDays, ...newDays]); // Duplicate list for looping
  }, [selectedMonth, selectedYear]);

  const centerScroll = (container, selectedValue) => {
    const items = Array.from(container.children);
    const selectedItem = items.find(
      (item) => item.textContent === selectedValue
    );
    if (selectedItem) {
      const containerHeight = container.clientHeight;
      const itemHeight = selectedItem.clientHeight;
      const offsetTop = selectedItem.offsetTop;

      const newScrollTop = offsetTop - containerHeight / 2 + itemHeight / 2;
      container.scrollTop = Math.max(
        0,
        Math.min(newScrollTop, container.scrollHeight - containerHeight)
      );
      updateItemStyles(container);
    }
  };

  const updateItemStyles = (container) => {
    const items = Array.from(container.children);
    const containerCenter = container.scrollTop + container.clientHeight / 2;

    items.forEach((item) => {
      const itemCenter = item.offsetTop + item.clientHeight / 2;
      const isCentered =
        Math.abs(itemCenter - containerCenter) < item.clientHeight / 2;
      item.classList.toggle("selected", isCentered);
      item.classList.remove("before", "after");
    });

    const selectedIndex = items.findIndex((item) =>
      item.classList.contains("selected")
    );
    if (selectedIndex > 0) items[selectedIndex - 1].classList.add("before");
    if (selectedIndex < items.length - 1)
      items[selectedIndex + 1].classList.add("after");
  };

  useEffect(() => {
    centerScroll(dayRef.current, selectedDay);
    centerScroll(monthRef.current, selectedMonth);
    centerScroll(yearRef.current, selectedYear);
  }, [selectedDay, selectedMonth, selectedYear]);

  const handleScroll = (e) => {
    const container = e.target;
    const scrollHeight = container.scrollHeight;
    const scrollTop = container.scrollTop;
    const clientHeight = container.clientHeight;

    // Check if user is scrolling near the start or end
    if (scrollTop < 100) {
      // User is at the top
      container.scrollTop += container.scrollHeight / 3;
    } else if (scrollTop + clientHeight > scrollHeight - 100) {
      // User is at the bottom
      container.scrollTop -= container.scrollHeight / 3;
    }

    updateItemStyles(container);
  };

  useEffect(() => {
    if (dayRef.current) dayRef.current.addEventListener("scroll", handleScroll);
    if (monthRef.current)
      monthRef.current.addEventListener("scroll", handleScroll);
    if (yearRef.current)
      yearRef.current.addEventListener("scroll", handleScroll);

    return () => {
      if (dayRef.current)
        dayRef.current.removeEventListener("scroll", handleScroll);
      if (monthRef.current)
        monthRef.current.removeEventListener("scroll", handleScroll);
      if (yearRef.current)
        yearRef.current.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const checkCenterItem = () => {
    if (dayRef.current) {
      const centeredDay = Array.from(dayRef.current.children).find((item) =>
        item.classList.contains("selected")
      )?.textContent;
      if (centeredDay && centeredDay !== selectedDay)
        setSelectedDay(centeredDay);
    }
    if (monthRef.current) {
      const centeredMonth = Array.from(monthRef.current.children).find((item) =>
        item.classList.contains("selected")
      )?.textContent;
      if (centeredMonth && centeredMonth !== selectedMonth)
        setSelectedMonth(centeredMonth);
    }
    if (yearRef.current) {
      const centeredYear = Array.from(yearRef.current.children).find((item) =>
        item.classList.contains("selected")
      )?.textContent;
      if (centeredYear && centeredYear !== selectedYear)
        setSelectedYear(centeredYear);
    }
  };

  useEffect(() => {
    const interval = setInterval(checkCenterItem, 100); // Check every 100ms
    return () => clearInterval(interval);
  }, [selectedDay, selectedMonth, selectedYear]);

  const handleDayChange = (day) => setSelectedDay(day);
  const handleMonthChange = (month) => setSelectedMonth(month);

  const handleYearChange = (year) => {
    setSelectedYear(year); // No condition for years now
  };

  const handleSave = () => {
    const formattedDate = `${selectedDay}/${
      monthOptions.indexOf(selectedMonth) + 1
    }/${selectedYear}`;
    onSave(formattedDate);
  };

  return (
    <Popup isOpen={isOpen} title={title} onClose={onClose}>
      <div className="popup-container p-0">
        <div className="custom-date-range">
          <div className="scroll-picker">
            {/* Month Picker */}
            <div className="scroll-container" ref={monthRef}>
              {monthOptions
                .concat(monthOptions, monthOptions)
                .map((month, index) => (
                  <div
                    key={index}
                    className={`scroll-item ${
                      month === selectedMonth ? "selected" : ""
                    }`}
                    onClick={() => handleMonthChange(month)}
                  >
                    {month}
                  </div>
                ))}
            </div>

            {/* Day Picker */}
            <div className="scroll-container" ref={dayRef}>
              {extendedDays.map((day, index) => (
                <div
                  key={index}
                  className={`scroll-item ${
                    day === selectedDay ? "selected" : ""
                  }`}
                  onClick={() => handleDayChange(day)}
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Year Picker */}
            <div className="scroll-container" ref={yearRef}>
              {yearOptions.map((year, index) => (
                <div
                  key={index}
                  className={`scroll-item ${
                    year === selectedYear ? "selected" : ""
                  }`}
                  onClick={() => handleYearChange(year)}
                >
                  {year}
                </div>
              ))}
            </div>
          </div>
          <div className="active-date-range"></div>
        </div>
        <button className="submit-button mt-4" onClick={handleSave}>
          Save
        </button>
      </div>
    </Popup>
  );
};

DateTimePopup.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  dayOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
  monthOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
  yearOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
};

DateTimePopup.defaultProps = {
  dayOptions: generateNumberArray(1, 1, new Date().getFullYear()), // Default day options from 1 to 31
  monthOptions: generateMonths(),
  yearOptions: generateArrayOfYears(), // Generate years from 1970 to the current year
};

export default DateTimePopup;
